'use client';

import { type PropsWithChildren, useMemo } from 'react';

import { DirectionProvider } from '@radix-ui/react-direction';
import { merge } from 'lodash';
import { NextIntlClientProvider, useLocale, useMessages, useTimeZone } from 'next-intl';

import { useConfig } from '@/components/providers/config-provider';
import { useLocaleMessages } from '@/components/providers/locale-messages-provider';
import { useLocaleDictionary } from '@/hooks/use-locale-dictionary';
import type { ClientSafeConfig } from '@/lib/types/tenant';
import { getTextDirection } from '@/lib/utils/locale-shared';
import { mergeLocaleOverrides } from '@/lib/utils/localization';

interface MessageLocaleWrapperProps extends PropsWithChildren {
  locale: string | undefined;
  /** When true, only provides locale context without adding a dir wrapper element. */
  inline?: boolean;
}

export function MessageLocaleWrapper({ locale, inline, children }: MessageLocaleWrapperProps) {
  const globalLocale = useLocale();
  const config = useConfig();

  if (!locale || locale === globalLocale) return <>{children}</>;

  const provider = (
    <MessageLocaleProvider locale={locale} translations={config.translations}>
      {children}
    </MessageLocaleProvider>
  );

  if (inline) return provider;

  const dir = getTextDirection(locale);
  return (
    <div dir={dir} lang={locale}>
      <DirectionProvider dir={dir}>{provider}</DirectionProvider>
    </div>
  );
}

function MessageLocaleProvider({
  locale,
  translations,
  children,
}: PropsWithChildren<{
  locale: string;
  translations?: ClientSafeConfig['translations'];
}>) {
  const timeZone = useTimeZone();
  // next-intl replaces messages rather than merging them, so the page's own
  // dictionary is the floor: without it every string under this provider is
  // missing until the fetch lands, and stays missing if it fails.
  const pageMessages = useMessages();
  const { dictionary } = useLocaleDictionary(locale);
  const isInlinedDictionary = locale in useLocaleMessages();

  const localeMessages = useMemo(() => {
    const overrides = translations as Record<string, unknown> | undefined;
    const base = merge({} as Record<string, unknown>, pageMessages, dictionary ?? {});
    // What decides this is where the dictionary came from, not what config holds.
    // An inlined dictionary carries no tenant wording, so it is applied here; a
    // fetched one already had it layered over English inside the route, and
    // re-applying config on top would let the tenant's English overwrite it.
    if (!isInlinedDictionary) {
      return base;
    }
    return mergeLocaleOverrides({ base, overrides, locale });
  }, [dictionary, isInlinedDictionary, locale, pageMessages, translations]);

  return (
    <NextIntlClientProvider locale={locale} messages={localeMessages} timeZone={timeZone}>
      {children}
    </NextIntlClientProvider>
  );
}
